/* eslint-disable class-methods-use-this */
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { get, noop, isNull, isEqual } from 'lodash-es';
import debounce from 'lodash-es/debounce';
import Card from 'reactstrap/lib/Card';
import Input from 'reactstrap/lib/Input';
import { TrackingHandler } from 'client/tracking/handler';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { buildTransactionCountPath, VehicleEntities, VehicleModel } from 'client/data/models/vehicle-v2';
import { RandomModel, RandomPaths, RandomPropTypes } from 'client/data/models/random';
import { VisitorEntities } from 'client/data/models/visitor';
import {
  CHART_PROPS_REDESIGN,
  EDMUNDS_PRICE_CHECKER_LOGO_PATH,
  PRICE_CHECKER_CREATIVE_ID,
  SLIDER_SECTION_LENGTH,
  PriceCheckerEntities,
} from 'site-modules/shared/constants/price-checker';
import { DEALS_TYPE_PRICE_LOGO } from 'site-modules/shared/components/deal/deal-constants';
import { getStaticImageUrl } from 'client/utils/image-helpers';
import { getPriceString } from 'site-modules/shared/utils/price-utils';
import {
  getDealBarIndex,
  getTmvChartData,
  getSliderValues,
  getTransactionCountCopyValues,
  getChartDescription,
} from 'site-modules/shared/utils/price-checker';
import {
  getDataPointsFromTransactionCount,
  getTransactionsCountOverlayConfig,
} from 'site-modules/shared/utils/pricing-chart';
import { PopoverItem } from 'site-modules/shared/components/popover-item/popover-item';
import { ZipWithDefaultActionHandling } from 'site-modules/shared/components/zip-with-default-action-handling/zip-with-default-action-handling';
import { PricingChart } from 'site-modules/shared/components/price-checker/price-checker-promo-chart/pricing-chart';
import { OdometerPaymentValue } from 'site-modules/shared/components/car-buying/odometer-payment-value/odometer-payment-value';
import { Gradient } from 'site-modules/shared/components/price-checker/price-checker-promo-chart/overlays/gradient/gradient';

import './price-checker-chart.scss';

const BARS_PER_DEAL = {
  DEFAULT: 5,
  WITH_COUNTS: 3,
};
const EMPTY_VALUES = [];

class PriceCheckerChartUI extends Component {
  static propTypes = {
    styleParams: PropTypes.shape({
      makeName: PropTypes.string,
      modelName: PropTypes.string,
      styleId: PropTypes.number,
      styleName: PropTypes.string,
    }),
    tmvData: VehicleEntities.NewTmv,
    tmvAdjustment: VehicleEntities.NewTmvAdjustment,
    transactionCount: VehicleEntities.StyleTransactionCount,
    transactionsCountAdjustments: RandomPropTypes.TransactionsCountAdjustments,
    visitorLocation: VisitorEntities.Location,
    userMsrp: PropTypes.number,
    userDealerPrice: PropTypes.number,
    bestDealPrice: PropTypes.number,
    setDealRated: PropTypes.func,
    setUserDealerPrice: PropTypes.func,
    isMobile: PropTypes.bool,
    sharedDeal: PriceCheckerEntities.SharedDataEntity,
    chartData: PropTypes.shape({}),
    mode: PropTypes.string,
    priceChartRef: PropTypes.shape({ current: PropTypes.object }),
    isCore6830International: PropTypes.bool,
  };

  static defaultProps = {
    styleParams: null,
    tmvData: null,
    tmvAdjustment: null,
    transactionCount: null,
    transactionsCountAdjustments: null,
    visitorLocation: null,
    userMsrp: null,
    userDealerPrice: null,
    bestDealPrice: null,
    setDealRated: null,
    setUserDealerPrice: noop,
    isMobile: false,
    sharedDeal: null,
    chartData: null,
    mode: '',
    priceChartRef: null,
    isCore6830International: false,
  };

  constructor(props) {
    super(props);

    this.chartInputsRef = React.createRef();
    this.chartSliderRef = React.createRef();

    this.state = {
      tmvChartData: null,
      sliderValues: EMPTY_VALUES,
      currentSliderValue: 0,
      // used in getDerivedStateFromProps to not calculate SliderValues
      prevMsrp: null,
    };
  }

  static getDerivedStateFromProps(
    { tmvData, tmvAdjustment, userMsrp, userDealerPrice, bestDealPrice, sharedDeal, isCore6830International },
    { prevMsrp, sliderValues }
  ) {
    if (!(tmvData && userDealerPrice && userMsrp) && !(sharedDeal && userDealerPrice && userMsrp)) {
      return {
        tmvChartData: null,
        sliderValues: EMPTY_VALUES,
        currentSliderValue: 0,
        prevMsrp: null,
      };
    }

    const calculatedTmvData = sharedDeal || tmvData;
    const tmvChartData = getTmvChartData({
      tmvData: calculatedTmvData,
      tmvAdjustment,
      userMsrp,
      userDealerPrice,
      bestDealPrice,
      isCore6830International,
    });

    if (userMsrp === prevMsrp && sliderValues.includes(userDealerPrice)) {
      return {
        currentSliderValue: sliderValues.indexOf(userDealerPrice),
        tmvChartData,
      };
    }

    const nextSliderValues = getSliderValues(tmvChartData, userDealerPrice, SLIDER_SECTION_LENGTH);

    return {
      tmvChartData,
      sliderValues: nextSliderValues,
      currentSliderValue: nextSliderValues.indexOf(userDealerPrice),
      prevMsrp: userMsrp,
    };
  }

  componentDidMount() {
    this.trackPriceCheckerResult();
  }

  componentDidUpdate(prevProps, prevState) {
    this.trackPriceCheckerResult();

    const { tmvChartData } = this.state;
    const { setDealRated } = this.props;

    if (!isEqual(tmvChartData, prevState.tmvChartData) && !!tmvChartData && setDealRated) {
      setDealRated({ tmvChartData });
    }
  }

  getInputPosition = () => {
    let position;
    const { sliderValues, currentSliderValue } = this.state;
    const inputWidth = get(this.chartInputsRef, 'current.offsetWidth');
    const sliderWidth = get(this.chartSliderRef, 'current.offsetWidth');
    if (inputWidth && sliderWidth) {
      position = ((inputWidth - sliderWidth) / (sliderValues.length - 1)) * currentSliderValue;
    }

    return position || 0;
  };

  handleSliderChange = e => {
    this.props.setUserDealerPrice(this.state.sliderValues[Number(e.target.value)]);
    this.trackSliderChange();
  };

  trackPriceCheckerResult = debounce(() => {
    const { tmvChartData } = this.state;

    if (!tmvChartData) {
      return;
    }

    TrackingHandler.fireEvent('pc_show_result', {
      event_data: {
        action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
        value: `${tmvChartData.dealerPrice.label.toLowerCase()}_price`,
        creative_id: PRICE_CHECKER_CREATIVE_ID,
      },
    });
  }, 500);

  handleChartClick = ({ nativeEvent: { offsetX, offsetY } }) =>
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_name: TrackingConstant.ACTION_TOOL_SELECT,
        subaction_name: TrackingConstant.SUBACTION_CHART_CLICK,
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        action_cause: TrackingConstant.ACTION_CAUSE_DRAG,
        creative_id: PRICE_CHECKER_CREATIVE_ID,
        value: `${offsetX},${offsetY}`,
      },
    });

  handleChartTouchEnd = ({ target, changedTouches }) => {
    const touch = get(changedTouches, '[0]');

    if (!touch) {
      return;
    }

    const { x, y } = target.getBoundingClientRect();

    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_name: TrackingConstant.ACTION_TOOL_SELECT,
        subaction_name: TrackingConstant.SUBACTION_CHART_CLICK,
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        action_cause: TrackingConstant.ACTION_CAUSE_DRAG,
        creative_id: PRICE_CHECKER_CREATIVE_ID,
        value: `${Math.round(touch.clientX - x)},${Math.round(touch.clientY - y)}`,
      },
    });
  };

  trackSliderChange = debounce(() => {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_name: TrackingConstant.ACTION_TOOL_SELECT,
        subaction_name: TrackingConstant.SLIDER_MOVE,
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        action_cause: TrackingConstant.ACTION_CAUSE_DRAG,
        creative_id: PRICE_CHECKER_CREATIVE_ID,
      },
    });
  }, 500);

  render() {
    const {
      styleParams,
      transactionCount,
      transactionsCountAdjustments,
      userDealerPrice,
      userMsrp,
      visitorLocation,
      isMobile,
      chartData,
      mode,
      priceChartRef,
      isCore6830International,
    } = this.props;
    const { tmvChartData, sliderValues, currentSliderValue } = this.state;

    const priceLabel = get(tmvChartData, 'dealerPrice.label', '');
    const priceLogo = DEALS_TYPE_PRICE_LOGO[priceLabel];
    const inputPosition = this.getInputPosition();
    const isChartDataAvailable = !isNull(chartData);
    const logo = get(chartData, 'logo', EDMUNDS_PRICE_CHECKER_LOGO_PATH);

    const { group, level } = getTransactionCountCopyValues({ transactionCount, styleParams, visitorLocation });
    const description = get(
      chartData,
      'description',
      getChartDescription({
        hasTmvChartData: !!tmvChartData,
        transactionsDetails: { group, level, hasTransactionCount: !!transactionCount },
        modelName: get(styleParams, 'modelName', ''),
        isCore6830International,
      })
    );

    const height = isChartDataAvailable ? 18 : 22;

    let barsPerDeal = BARS_PER_DEAL.DEFAULT;
    let overlayPricingChartProps = { overlayConfig: CHART_PROPS_REDESIGN.COMMON.overlayConfig };
    let transactionCountChartProps = {
      dataPoints: tmvChartData
        ? CHART_PROPS_REDESIGN.DATA_POINTS.WITH_DATA
        : CHART_PROPS_REDESIGN.DATA_POINTS.WITHOUT_DATA,
    };
    if (transactionCount && tmvChartData) {
      const currentTransactionCount = Object.values(get(transactionCount, 'transactionCount', {})).some(value => value)
        ? get(transactionCount, 'transactionCount')
        : get(transactionCount, 'nationalTransactionCount');

      barsPerDeal = BARS_PER_DEAL.WITH_COUNTS;
      transactionCountChartProps = {
        columnWidth: { desktop: 34, mobile: 27 },
        dataPoints: getDataPointsFromTransactionCount({
          transactionCount: currentTransactionCount,
          transactionsCountAdjustments,
          barsPerDeal,
          minValue: 0,
          maxValue: 20,
        }),
        isStepGradient: true,
      };

      overlayPricingChartProps = {
        yStartingPoint: 110,
        arrowLineTopSpace: 35,
        overlayConfig: getTransactionsCountOverlayConfig({
          transactionCount: currentTransactionCount,
          component: Gradient,
          barsPerDeal,
        }),
      };
    }

    let tmvChartDataProps = {};
    if (tmvChartData) {
      tmvChartDataProps = {
        customPriceLabels: [
          {
            label: getPriceString(tmvChartData.maxGreatPrice),
            index: barsPerDeal,
          },
          {
            label: getPriceString(tmvChartData.maxFairPrice),
            index: barsPerDeal * 3 - 1,
            end: true,
          },
          !isMobile && {
            label: getPriceString(tmvChartData.maxGoodPrice),
            index: barsPerDeal * 2,
          },
        ].filter(value => value),
        selectedPriceIndex: getDealBarIndex({
          tmvData: tmvChartData,
          userDealerPrice,
          label: get(tmvChartData, 'dealerPrice.label'),
          barsPerDeal,
          sliderValues,
          currentSliderValue,
        }),
        selectedPricePercent: parseFloat((((currentSliderValue + 1) / sliderValues.length) * 100).toFixed(2)),
        selectedPrice: get(tmvChartData, 'dealerPrice'),
      };
    }

    return (
      <Fragment>
        <Card className="price-checker-chart" innerRef={priceChartRef}>
          <div
            className="header rounded-top d-flex justify-content-center align-items-center p-1"
            style={{ backgroundColor: get(tmvChartData, 'dealerPrice.bgColor') || get(chartData, 'color') }}
          >
            {tmvChartData ? (
              <img height={height} src={priceLogo} alt={`${priceLabel} price`} />
            ) : (
              <img height={height} src={getStaticImageUrl(logo)} alt="Edmunds logo" />
            )}
          </div>

          <div className="rounded-bottom px-1 pt-1 pb-2">
            <div className="d-flex flex-column align-items-center justify-content-end chart-heading heading-5-inline">
              {tmvChartData ? (
                <div>
                  <div className="d-inline-block">{tmvChartData.dealerPrice.cta}</div>
                  {!!tmvChartData.dealerPrice.tooltip && (
                    <PopoverItem className="text-primary-darker" screenReaderTooltipLabel="dealer price">
                      {tmvChartData.dealerPrice.tooltip}
                    </PopoverItem>
                  )}
                </div>
              ) : (
                <div className="heading-4">{get(chartData, 'cta', 'Enter prices to rate a deal')}</div>
              )}
              {/* 'd-none' to have animation on first input */}
              <div
                className={classnames('user-price text-primary-darker heading-2', {
                  'd-none': !tmvChartData,
                  'd-flex align-items-center justify-content-center': tmvChartData,
                })}
              >
                <OdometerPaymentValue value={userDealerPrice} />
              </div>
            </div>

            <div className="pos-r">
              <PricingChart
                {...CHART_PROPS_REDESIGN.COMMON}
                {...tmvChartDataProps}
                {...overlayPricingChartProps}
                {...transactionCountChartProps}
                chartHeight={null}
                yRate={2.75}
                isMobile={isMobile}
                idPrefix={mode}
              />
              {!!tmvChartData && (
                <Input
                  id={`${mode}sliderRangeInput`}
                  type="range"
                  min={0}
                  max={sliderValues.length - 1}
                  value={currentSliderValue}
                  onChange={this.handleSliderChange}
                  onClick={this.handleChartClick}
                  onTouchEnd={this.handleChartTouchEnd}
                  className="pos-a top-0 chart-custom-range"
                  aria-label="Enter dealer price quote"
                />
              )}
            </div>

            <div
              className={classnames('chart-inputs pos-r mt-1', {
                invisible: !tmvChartData,
              })}
              data-tracking-parent="slider"
            >
              <Input
                id={`${mode}sliderRangeInput`}
                type="range"
                min={0}
                max={sliderValues.length - 1}
                value={currentSliderValue}
                onChange={this.handleSliderChange}
                className="pos-r py-1"
                aria-label="Enter dealer price quote"
              />
              <div className="slider-range-colors left-0 right-0" />
              <div className="chart-slider pos-a top-0 left-0 right-0" ref={this.chartInputsRef}>
                {isMobile ? (
                  <div
                    style={{ left: inputPosition }}
                    className="slider-control ed-zdepth-3 bg-white d-flex justify-content-center align-items-center pos-a"
                    ref={this.chartSliderRef}
                  >
                    <span />
                    <span />
                    <span />
                  </div>
                ) : (
                  <div
                    style={{ left: inputPosition }}
                    className="slider-value-wrapper text-gray-darker ed-zdepth-3 bg-white px-0_5 py-0_75 d-flex justify-content-between align-items-center small pos-a"
                    ref={this.chartSliderRef}
                  >
                    <i className="icon-arrow-left3 size-10" aria-hidden />
                    <div className="px-0_25">{getPriceString(userDealerPrice)}</div>
                    <i className="icon-arrow-right3 size-10" aria-hidden />
                  </div>
                )}
              </div>
            </div>

            <div
              className={classnames({
                'px-1 mt-1_5': !isChartDataAvailable,
                'px-0_5': isChartDataAvailable,
              })}
            >
              <Card
                className={classnames('d-block small text-gray-darker text-center', {
                  'px-1_5 py-0_75': !isChartDataAvailable,
                  'fst-italic border-0': isChartDataAvailable,
                })}
              >
                {get(tmvChartData, 'maxGoodPrice') > userMsrp && (
                  <span className="fw-bold">
                    High demand is causing the market average price paid to be above MSRP.&nbsp;
                  </span>
                )}
                {description}{' '}
                {!isCore6830International && (
                  <Fragment>
                    {get(visitorLocation, 'city')}, {get(visitorLocation, 'stateCode')}{' '}
                  </Fragment>
                )}
                <ZipWithDefaultActionHandling
                  buttonClassName={classnames('color-primary-darker small fw-normal text-transform-none', {
                    'border-0': isChartDataAvailable,
                  })}
                  buttonText={isCore6830International ? 'zip code' : undefined}
                />
              </Card>
            </div>
          </div>
        </Card>
      </Fragment>
    );
  }
}

const mapStateToProps = state => ({
  visitorLocation: get(state, 'visitor.location', {}),
});

export const PriceCheckerChart = connect(mapStateToProps)(
  connectToModel(React.memo(PriceCheckerChartUI, isEqual), {
    transactionCount: bindToPath(
      ({ styleParams, isCore6830International }) =>
        !isCore6830International && !!get(styleParams, 'styleId') && buildTransactionCountPath(styleParams.styleId),
      VehicleModel
    ),
    transactionsCountAdjustments: bindToPath(
      () => RandomPaths.getTransactionsCountAdjustments(BARS_PER_DEAL.WITH_COUNTS),
      RandomModel
    ),
  })
);
